<script setup lang="ts">
/**
 * Run-form control for a `party_select` workflow input. Reuses the Parties feature
 * (`useParties` → the org's extracted parties) so the user can pick an existing
 * company/person, or enter one manually. Emits `{ name, idNumber, address }` — the shape
 * `buildScope` exposes as `${key.name}` / `${key.idNumber}` / `${key.address}`.
 *
 * The search is a `ui/combobox` (MAR-948): the arrow keys reach the options and Enter picks one,
 * which the hand-rolled list of buttons it replaced never allowed.
 */
import { Building2, UserRound, Search, X, Pencil } from 'lucide-vue-next'
import { useParties, type ExtractedParty, type PartyKind } from '~/composables/useParties'
import {
  Combobox, ComboboxAnchor, ComboboxInput, ComboboxItem, ComboboxList,
} from '~/components/ui/combobox'

export interface PartyValue { name: string; idNumber: string; address?: string }

const model = defineModel<PartyValue | null>({ default: null })
const props = defineProps<{ partyKind?: PartyKind }>()
const { t } = useI18n()

const { parties, loading, fetchParties } = useParties()
const search = ref('')
const manual = ref(false)

onMounted(fetchParties)

const results = computed(() => {
  const q = search.value.trim().toLowerCase()
  return parties.value
    .filter(p => !props.partyKind || p.kind === props.partyKind)
    .filter(p => !q || p.name.toLowerCase().includes(q) || p.idNumber.toLowerCase().includes(q))
    .slice(0, 8)
})

const selected = computed(() => !manual.value && !!model.value?.name?.trim())

function pick(p: ExtractedParty) {
  model.value = { name: p.name, idNumber: p.idNumber, address: p.address ?? '' }
  manual.value = false
  search.value = ''
}
function clear() {
  model.value = null
  manual.value = false
  search.value = ''
}
function startManual() {
  manual.value = true
  model.value = model.value ?? { name: '', idNumber: '', address: '' }
}
function setField(k: keyof PartyValue, v: string) {
  model.value = { name: '', idNumber: '', address: '', ...(model.value ?? {}), [k]: v }
}
</script>

<template>
  <div class="space-y-2">
    <!-- Selected party card -->
    <div v-if="selected" class="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-2.5">
      <component :is="partyKind === 'person' ? UserRound : Building2" class="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-medium">{{ model?.name }}</p>
        <p v-if="model?.idNumber" class="truncate text-xs text-muted-foreground">{{ model.idNumber }}</p>
        <p v-if="model?.address" class="truncate text-xs text-muted-foreground">{{ model.address }}</p>
      </div>
      <button type="button" class="rounded-md p-1 text-muted-foreground hover:text-destructive" :title="t('common.remove')" @click="clear">
        <X class="h-4 w-4" />
      </button>
    </div>

    <!-- Manual entry -->
    <div v-else-if="manual" class="space-y-2 rounded-md border border-border p-2.5">
      <WorkflowLabeledInput :model-value="model?.name ?? ''" :placeholder="t('workflowRun.partySelect.namePlaceholder')" @update:model-value="(v: string | number) => setField('name', String(v))" />
      <WorkflowLabeledInput :model-value="model?.idNumber ?? ''" :placeholder="t('workflowRun.partySelect.idNumberPlaceholder')" @update:model-value="(v: string | number) => setField('idNumber', String(v))" />
      <WorkflowLabeledInput :model-value="model?.address ?? ''" :placeholder="t('workflowRun.partySelect.addressPlaceholder')" @update:model-value="(v: string | number) => setField('address', String(v))" />
      <button type="button" class="text-xs text-muted-foreground hover:text-foreground" @click="clear">{{ t('workflowRun.partySelect.pickFromDocuments') }}</button>
    </div>

    <!-- Search + pick from extracted parties. `ignore-filter`: `results` above is the filter (name OR
         id, narrowed by `partyKind`, capped at 8), recomputed as the org's parties load. -->
    <div v-else>
      <Combobox ignore-filter open-on-focus>
        <ComboboxAnchor>
          <Search class="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <ComboboxInput
            v-model="search"
            class="pl-8"
            :display-value="() => ''"
            :placeholder="loading ? t('common.loading') : t('workflowRun.partySelect.searchPlaceholder')"
          />
        </ComboboxAnchor>
        <ComboboxList hide-when-empty>
          <ComboboxItem v-for="p in results" :key="p.key" :value="p.key" @select="pick(p)">
            <component :is="p.kind === 'person' ? UserRound : Building2" class="h-4 w-4 text-muted-foreground" />
            <span class="min-w-0 flex-1 truncate">{{ p.name }}</span>
            <span v-if="p.idNumber" class="shrink-0 text-xs text-muted-foreground">{{ p.idNumber }}</span>
          </ComboboxItem>
        </ComboboxList>
      </Combobox>
      <button type="button" class="mt-1.5 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" @click="startManual">
        <Pencil class="h-3 w-3" /> {{ t('workflowRun.partySelect.enterManually') }}
      </button>
    </div>
  </div>
</template>
